import {
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   OnInit,
   Output
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { Task } from '../../domain/task';
import { WorkflowType } from '../../domain/enums/workflow-type';
import { PhobosTaskInstance } from '../../domain/phobos-task-instance';
import {
   ReportViewerAvailability,
   ReportViewerKind
} from '../../domain/reporting/report-viewer.models';
import { PermissionsStoreService } from '../../services/stores/permission-store-service';
import { UserPermissions } from '../../domain/security/user-permissions';
import { Group } from '../../domain/group';

@Component({
   selector: 'app-task-report-panel',
   templateUrl: './task-report-panel.component.html',
   styleUrls: ['./task-report-panel.component.scss']
})
export class TaskReportPanelComponent implements OnInit, OnDestroy {
   @Input({ required: true }) currentTask!: Task;

   @Input() isLoading: boolean = false;
   @Input() isPrimary: boolean = true;
   @Input() isSubmitDisabled: boolean = false;

   // Layout
   @Input() viewerPanelInFocus: boolean = true;
   @Input() topSpacerClass: string = 'h-4rem';

   // Viewer state
   @Input({ required: true }) viewerAvailability!: ReportViewerAvailability;
   @Input() activeViewer: ReportViewerKind = 'pdf';

   // Context / behaviour
   @Input() isPhobosTask: boolean = false;
   @Input() phobosTaskInstance?: PhobosTaskInstance;

   // Back navigation
   @Input() fallbackBackUrl: string = '/dashboard';

   // For styling the form action row (keeps your existing phobos layout behaviour)
   @Input() formActionsContainerClass: string = 'flex justify-content-end gap-3';

   // Outputs (events)
   @Output() nextRequested = new EventEmitter<void>();
   @Output() previousRequested = new EventEmitter<void>();
   @Output() reviewRequested = new EventEmitter<void>();

   @Output() groupChanged = new EventEmitter<Group>();
   @Output() assignRequested = new EventEmitter<MouseEvent>();
   @Output() completeRequested = new EventEmitter<MouseEvent>();
   @Output() reopenRequested = new EventEmitter<MouseEvent>();
   @Output() resendRequested = new EventEmitter<MouseEvent>();

   @Output() activeViewerChanged = new EventEmitter<ReportViewerKind>();

   protected readonly WorkflowType = WorkflowType;

   private subscriptions = new Subscription();
   private backUrlFromQueryParam: string | null = null;

   public userPermissions: UserPermissions = new UserPermissions();

   constructor(
      private router: Router,
      private route: ActivatedRoute,
      private permissionsStore: PermissionsStoreService
   ) {}

   async ngOnInit(): Promise<void> {
      // Read "from" query param once (used for back navigation)
      this.subscriptions.add(
         this.route.queryParams.subscribe((params) => {
            if (!this.backUrlFromQueryParam && params['from']) {
               this.backUrlFromQueryParam = params['from'];
            }
         })
      );

      // Permissions (for showing Revert button safely)
      try {
         const permissions = await firstValueFrom(this.permissionsStore.get());
         this.userPermissions = permissions ?? new UserPermissions();
      } catch {
         this.userPermissions = new UserPermissions();
      }
   }

   ngOnDestroy(): void {
      this.subscriptions.unsubscribe();
   }

   // -------------------------
   // Viewer-aware gating
   // -------------------------
   get hasActiveReport(): boolean {
      if (!this.viewerAvailability) return false;

      return (
         (this.activeViewer === 'pdf' && this.viewerAvailability.hasPdfReport) ||
         (this.activeViewer === 'html' && this.viewerAvailability.hasHtmlReport)
      );
   }

   get canShowViewerToggle(): boolean {
      return (
         !!this.viewerAvailability?.hasPdfReport &&
         !!this.viewerAvailability?.hasHtmlReport
      );
   }

   setActiveViewer(viewer: ReportViewerKind): void {
      if (viewer === this.activeViewer) return;

      // Only allow switching to a viewer that is available
      if (viewer === 'pdf' && !this.viewerAvailability.hasPdfReport) return;
      if (viewer === 'html' && !this.viewerAvailability.hasHtmlReport) return;

      this.activeViewer = viewer;
      this.activeViewerChanged.emit(viewer);
   }

   // -------------------------
   // Back + navigation
   // -------------------------
   onBackClicked(): void {
      if (this.backUrlFromQueryParam) {
         this.router.navigateByUrl(this.backUrlFromQueryParam);
         return;
      }

      this.router.navigate([this.fallbackBackUrl]);
   }

   onNextClicked(): void {
      this.nextRequested.emit();
   }

   onPreviousClicked(): void {
      this.previousRequested.emit();
   }

   // -------------------------
   // Action visibility rules
   // (Important: do NOT rely on PDF documentId anymore)
   // -------------------------
   private get isFormTask(): boolean {
      return this.currentTask?.type === WorkflowType.Form;
   }

   private get isAssignedToCurrentUser(): boolean {
      return !!this.currentTask?.isAssignedToUser;
   }

   private get hasCompletedDate(): boolean {
      return !!this.phobosTaskInstance?.completedDate;
   }

   get canReviewNonFormTask(): boolean {
      return (
         this.isPrimary &&
         this.hasActiveReport &&
         this.isAssignedToCurrentUser &&
         !this.isFormTask &&
         this.currentTask?.isComplete !== true
      );
   }

   get canReviewFormTask(): boolean {
      if (!this.isPhobosTask) return false;

      // Matches your existing intent:
      // show if assigned + Form + (not complete OR no completedDate)
      const isCompleteFlag = this.currentTask?.isComplete === true;

      return (
         this.isPrimary &&
         this.hasActiveReport &&
         this.isAssignedToCurrentUser &&
         this.isFormTask &&
         (!isCompleteFlag || !this.hasCompletedDate)
      );
   }

   get canShowResendAndRevertRow(): boolean {
      return (
         this.isPhobosTask &&
         this.isPrimary &&
         this.isFormTask &&
         this.hasCompletedDate
      );
   }

   get canShowRevertButton(): boolean {
      return !!this.userPermissions?.forms?.admin;
   }

   // -------------------------
   // Action events
   // -------------------------
   onReviewClicked(): void {
      this.reviewRequested.emit();
   }

   onGroupChange(group: Group): void {
      this.groupChanged.emit(group);
   }

   onAssignClicked(event: MouseEvent): void {
      event.preventDefault();
      this.assignRequested.emit(event);
   }

   onCompleteClicked(event: MouseEvent): void {
      event.preventDefault();
      this.completeRequested.emit(event);
   }

   onReopenClicked(event: MouseEvent): void {
      event.preventDefault();
      this.reopenRequested.emit(event);
   }

   onResendClicked(event: MouseEvent): void {
      event.preventDefault();
      this.resendRequested.emit(event);
   }

   // Spacer: show when primary, active report exists, and no action row is visible
   get shouldShowTopSpacer(): boolean {
      const hasAnyPrimaryActionRow =
         this.canReviewNonFormTask ||
         this.canReviewFormTask ||
         this.canShowResendAndRevertRow;

      return this.isPrimary && this.hasActiveReport && !hasAnyPrimaryActionRow;
   }
}
